import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

import { apiRequest } from '@/src/services';
import { getZeroOption, type Option, getValidSelectValue } from '@/src/utils';
import type {
    InitialOptions,
    ClassOption,
    SchoolClassOptions,
    FilterState,
} from '@/src/pages/rostering/users/models/filterDTOs';
import {
    SimpleButton,
    SimpleSingleSelect,
    ErrorMessage,
    FilterIcon,
    ICON_COLORS,
    FilterBarContainer,
} from '@/src/ui';

interface FiltersProps {
    refreshTableData: (
        userTypeMasterId: number,
        classId: number,
        districtId?: number,
        schoolId?: number,
        gradeLevelMasterId?: number
    ) => void;
    setSearchString: (val: string) => void;
    filterState: FilterState;
    setFilterState: React.Dispatch<React.SetStateAction<FilterState>>;
}

const AllOption = getZeroOption('All');
const LoadingOption = getZeroOption('Loading...');
const DefaultSchoolOption = getZeroOption('Select District First');
const DefaultClassOption = getZeroOption('Select School First');
const NoClassOption = getZeroOption('No Classes Available');

const Filters: React.FC<FiltersProps> = ({ refreshTableData, filterState, setFilterState }) => {
    const [error, setError] = useState('');

    const [userTypeMasterOptions, setUserTypeMasterOptions] = useState<Array<Option>>([
        LoadingOption,
    ]);
    const [gradeLevelMasterOptions, setGradeLevelMasterOptions] = useState<Array<Option>>([
        LoadingOption,
    ]);
    const [districtOptions, setDistrictOptions] = useState<Array<Option>>([LoadingOption]);
    const [schoolOptions, setSchoolOptions] = useState<Array<Option>>([AllOption]);
    const [classOptions, setClassOptions] = useState<Array<Option>>([AllOption]);
    const [classData, setClassData] = useState<Array<ClassOption>>([]);

    const [fieldsChanged, setFieldsChanged] = useState(false);

    useEffect(() => {
        (async function init() {
            try {
                const res = await apiRequest<InitialOptions>(
                    'GET',
                    'utility/filterData?getUserTypeMaster=true&getGradeLevelMaster=true&getDistrict=true'
                );
                const data = res.data;

                const utmOptions = data.userTypeMasterOptions;
                if (!utmOptions) {
                    setUserTypeMasterOptions([LoadingOption]);
                    throw new Error('No user type options found');
                }
                setUserTypeMasterOptions(data.userTypeMasterOptions);

                setFilterState((prev) => ({
                    ...{
                        userTypeMasterId: data.userTypeMasterOptions[0]?.id || 0,
                        gradeLevelMasterId: Number(AllOption.id),
                        districtId: Number(AllOption.id),
                        schoolId: Number(AllOption.id),
                        classId: Number(AllOption.id),
                    },
                    ...prev,
                }));

                setGradeLevelMasterOptions([AllOption, ...data.gradeLevelMasterOptions]);
                const newDistrictOptions = data.districtOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setDistrictOptions([AllOption, ...newDistrictOptions]);
                setError('');
            } catch {
                setError('Failed to fetch grade and district options.');
            }
        })();

        if (filterState && filterState.districtId && filterState.districtId !== AllOption.id) {
            fetchSchoolClassOptions(filterState.districtId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Run when district is changed
    const fetchSchoolClassOptions = async (districtId: number | string) => {
        // Reset filters if needed
        if (!districtId) {
            setSchoolOptions([DefaultSchoolOption]);
            setFilterState((prev) => ({ ...prev, schoolId: Number(DefaultSchoolOption.id) }));
            setClassOptions([DefaultClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(DefaultClassOption.id) }));
            return;
        }

        (async function fetchData() {
            try {
                const res = await apiRequest<SchoolClassOptions>(
                    'GET',
                    `utility/filterData?getSchool=true&getClass=true&districtId=${districtId}`
                );
                const data = res.data;

                const newSchoolOptions = data.schoolOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setSchoolOptions([AllOption, ...newSchoolOptions]);

                setFilterState((prev) => {
                    const schoolExists = newSchoolOptions.some(
                        (opt) => Number(opt.id) === Number(prev.schoolId)
                    );
                    return {
                        ...prev,
                        schoolId: schoolExists ? prev.schoolId : Number(AllOption.id),
                    };
                });

                const sortedClassOptions = data.classOptions.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
                setClassData(sortedClassOptions);
                updateClassOptions(
                    sortedClassOptions,
                    filterState.gradeLevelMasterId,
                    filterState.schoolId
                );
                setError('');
            } catch {
                setError('Failed to fetch school and class options');
            }
        })();
    };

    // Run when school is changed
    const updateClassOptions = (
        data: Array<ClassOption>,
        gradeLevelMasterId: number | string,
        schoolId: number | string
    ) => {
        // Reset filter if needed
        if (schoolId === DefaultSchoolOption.id) {
            setClassOptions([DefaultClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(DefaultClassOption.id) }));
            return;
        }

        const newClassOptions = data
            .filter((option) => {
                if (schoolId && option.schoolId !== Number(schoolId)) {
                    return false;
                }
                const glids = option.gradeLevelMasterIds;
                const doGradeOptionsExist = glids && glids.length > 0;
                if (
                    gradeLevelMasterId &&
                    doGradeOptionsExist &&
                    !glids.includes(Number(gradeLevelMasterId))
                ) {
                    return false;
                }
                return true;
            })
            .map((option) => ({
                id: option.id,
                name: option.name,
            }));

        if (!newClassOptions.length) {
            setClassOptions([NoClassOption]);
            setFilterState((prev) => ({ ...prev, classId: Number(NoClassOption.id) }));
            return;
        }

        setClassOptions([AllOption, ...newClassOptions]);
        setFilterState((prev) => {
            const classExists = newClassOptions.some(
                (opt) => Number(opt.id) === Number(prev.classId)
            );
            return {
                ...prev,
                classId: classExists ? prev.classId : Number(AllOption.id),
            };
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                justifyContent: 'center',
                mb: 2,
            }}
        >
            <FilterBarContainer>
                <SimpleSingleSelect
                    name="user-type-select"
                    label="User Type"
                    value={Number(
                        getValidSelectValue(filterState.userTypeMasterId, userTypeMasterOptions, 1)
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({
                            ...prev,
                            userTypeMasterId: Number(e.target.value),
                        }));
                    }}
                    options={userTypeMasterOptions}
                    error={error ? { message: error } : undefined}
                    sx={{ minWidth: 140, maxWidth: 280, height: 40, alignSelf: 'center' }}
                />
                <SimpleSingleSelect
                    name="grade-level-select"
                    label="Grade Level"
                    value={Number(
                        getValidSelectValue(
                            filterState.gradeLevelMasterId,
                            gradeLevelMasterOptions,
                            AllOption.id
                        )
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        const newGradeLevelMasterId = Number(e.target.value);
                        setFilterState((prev) => ({
                            ...prev,
                            gradeLevelMasterId: newGradeLevelMasterId,
                        }));
                        updateClassOptions(classData, newGradeLevelMasterId, filterState.schoolId);
                    }}
                    options={gradeLevelMasterOptions}
                    sx={{ minWidth: 140, maxWidth: 280, height: 40, alignSelf: 'center' }}
                />
                <SimpleSingleSelect
                    name="district-select"
                    label="District"
                    value={Number(
                        getValidSelectValue(filterState.districtId, districtOptions, AllOption.id)
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, districtId: Number(e.target.value) }));
                        fetchSchoolClassOptions(Number(e.target.value));
                    }}
                    options={districtOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="school-select"
                    label="School"
                    value={Number(
                        getValidSelectValue(filterState.schoolId, schoolOptions, AllOption.id)
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, schoolId: Number(e.target.value) }));
                        updateClassOptions(
                            classData,
                            filterState.gradeLevelMasterId,
                            Number(e.target.value)
                        );
                    }}
                    options={schoolOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleSingleSelect
                    name="class-select"
                    label="Class"
                    value={Number(
                        getValidSelectValue(filterState.classId, classOptions, AllOption.id)
                    )}
                    onChange={(e) => {
                        setFieldsChanged(true);
                        setFilterState((prev) => ({ ...prev, classId: Number(e.target.value) }));
                    }}
                    options={classOptions}
                    sx={{ minWidth: 140, maxWidth: 280 }}
                />
                <SimpleButton
                    onClick={() => {
                        setFieldsChanged(false);
                        refreshTableData(
                            filterState.userTypeMasterId,
                            filterState.gradeLevelMasterId,
                            filterState.districtId,
                            filterState.schoolId,
                            filterState.classId
                        );
                    }}
                    variant={fieldsChanged ? 'outlined' : 'contained'}
                    color="primary"
                    sx={{ minHeight: 40, height: 40, alignSelf: 'center' }}
                >
                    <FilterIcon
                        color={fieldsChanged ? ICON_COLORS.primary : ICON_COLORS.secondary}
                        style={{ width: '16px', height: '16px', marginRight: '8px' }}
                    />{' '}
                    Filter
                </SimpleButton>
            </FilterBarContainer>
            <Box sx={{ display: 'flex', justifyContent: 'right' }}>
                <ErrorMessage error={error} />
            </Box>
        </Box>
    );
};

export default Filters;
